import  {useEffect, useRef, useState} from 'react';
import api from '../Interceptor';
import {getRoute} from '../api/routes';
import {toast} from 'react-toastify';
import {io} from 'socket.io-client';
import {CustomInput} from '../components/ui';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlus} from '@fortawesome/free-solid-svg-icons';
import AddSubUser from '../components/AddSubUser';
import useModal from '../hooks/useModal';
import {FaAngleDown, FaAngleUp} from 'react-icons/fa6';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const socket = io(API_BASE_URL);

const Chat = () => {
  const loggedInUserId = localStorage.getItem('id');
  const [userList, setUserList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const messagesEndRef = useRef(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [addUserModalVisible, setAddUserModalVisible] = useState(false);
  const {isOpen, toggleModal} = useModal();
  const [isMobileView, setIsMobileView] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [isUserListCollapsed, setIsUserListCollapsed] = useState(false);

  useEffect(() => {
    const checkSize = () => {
      setIsMobileView(window.innerWidth < 1024); // mobile + small tablets
    };
    checkSize();
    window.addEventListener('resize', checkSize);

    return () => window.removeEventListener('resize', checkSize);
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `${getRoute('users', 'admin', 'users', false, false, '')}`
      );
      setUserList(response.data);
      setLoading(false);
    } catch (error) {
      if (error?.isUnpaid) {
        return;
      }
      toast.error('Oops! Error while fetching Members');
    } finally {
      setLoading(false);
    }
  };
  const fetchUnreadCounts = async () => {
    try {
      const response = await api.get(
        getRoute('users', 'admin', 'message', false) +
          `/unread-count/${loggedInUserId}`
      );
      const counts = {};
      response.data.forEach((item) => {
        counts[item._id] = item.count;
      });
      setUnreadCounts(counts);
    } catch (error) {
      if (error.isUnpaid) {
        return;
      }
      console.error('Failed to fetch unread counts', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchUnreadCounts();
  }, []);
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({behavior: 'smooth'});
    }
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const fetchChatData = async () => {
      if (!activeChat || !activeChat.id) return;

      try {
        const response = await api.get(
          `${getRoute('users', 'admin', 'message', false)}?receiver=${
            activeChat.id
          }`
        );

        if (Array.isArray(response.data)) {
          setMessages(response.data);
        }
      } catch (error) {
        console.error('Error fetching chat data:', error);
        toast.error('Failed to fetch messages.');
      }
    };

    fetchChatData();
  }, [activeChat]);

  useEffect(() => {
    const markMessagesAsRead = async () => {
      if (!activeChat || !activeChat.id) return;

      try {
        await api.put(
          getRoute('users', 'admin', 'message', false) + `/mark-read`,
          {
            sender: activeChat.id,
            receiver: loggedInUserId,
          }
        );
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    };

    markMessagesAsRead();
  }, [activeChat]);

  useEffect(() => {
    if (!messages.length || !activeChat) return;

    // Find if there are unread messages sent by the current active user
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.sender === activeChat.id) {
      api
        .put(getRoute('users', 'admin', 'message', false) + `/mark-read`, {
          sender: activeChat.id,
          receiver: loggedInUserId,
        })
        .then(fetchUnreadCounts);
    }
  }, [messages]);

  useEffect(() => {
    const handleReceiveMessage = (data) => {
      if (!data || !data.content) return;

      // ✅ Check if this message belongs to the active chat
      const isRelevant =
        (data.sender === activeChat?.id && data.receiver === loggedInUserId) ||
        (data.receiver === activeChat?.id && data.sender === loggedInUserId);

      if (isRelevant) {
        setMessages((prev) => [...prev, data]);
      }

      // Optionally update unread count for others
      if (data.sender !== activeChat?.id) {
        fetchUnreadCounts();
      }
    };

    socket.on('receiveMessage', handleReceiveMessage);

    return () => {
      socket.off('receiveMessage', handleReceiveMessage);
    };
  }, [activeChat]);

  const handleSendMessage = async () => {
    if (!currentMessage.trim()) {
      return;
    }

    const newMessage = {
      receiver: activeChat.id,
      content: currentMessage,
    };

    try {
      const response = await api.post(
        getRoute('users', 'admin', 'message', false),
        newMessage
      );

      socket.emit('sendMessage', response.data);
      setCurrentMessage('');

      // reset textarea height after clearing message (Slack-like)
      const inputEl = document.getElementById('chat-input');
      if (inputEl) {
        inputEl.style.height = '40px';
        inputEl.style.overflowY = 'hidden';
      }
    } catch (error) {
      toast.error('Failed to send message.');
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  const handleClearSearch = () => {
    setSearchTerm('');
  };
  const filteredUsers = userList.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  useEffect(() => {
    if (!isMobileView && userList.length === 1 && !activeChat) {
      const onlyUser = userList[0];
      setActiveChat({
        type: 'subuser',
        id: onlyUser._id,
        name: onlyUser.name,
      });
      setShowChatPanel(true);
    }
  }, [userList, activeChat, isMobileView]);
  const handleSuccess = () => {
    setAddUserModalVisible(false);
    fetchUsers();
  };
  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <div className="bg-gray-50 h-screen flex flex-col gap-3 overflow-hidden font-customFont">
      {/* Team Messages Card */}
      <div
        className={`group relative m-3 mb-0 overflow-hidden border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-100 shadow-lg transition-all duration-300 ease-in-out  rounded-2xl p-4 pb-2`}
      >
        {/* Decorative Background Element */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-blue-200 rounded-full -mr-16 -mt-16 opacity-20"></div>

        {/* Card Header */}
        <div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between 
          gap-4 mb-3 relative z-10"
        >
          {/* Left Section */}
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl 
              bg-gradient-to-br from-blue-500 to-blue-700 
              flex items-center justify-center shadow-md"
            >
              <i className="fa-solid fa-comments text-white text-lg sm:text-xl"></i>
            </div>

            <div>
              <p
                className="text-blue-700 font-bold uppercase tracking-wide
              text-sm sm:text-base lg:text-lg"
              >
                Chats
              </p>
              <p
                className="text-blue-600 font-medium
              text-xs sm:text-sm"
              >
                {filteredUsers.length} team members
              </p>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex sm:items-center gap-2 w-full sm:w-auto">
            {/* Search */}
            <div className="relative w-full sm:w-64 h-10">
              <CustomInput
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                className="h-10 pl-3 pr-8 w-full rounded-lg text-sm 
             border border-blue-300 bg-white/80
             focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search team members..."
              />

              {searchTerm && (
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-2 
               text-blue-400 hover:text-blue-600"
                  onClick={handleClearSearch}
                >
                  <i className="fa-solid fa-times text-sm" />
                </button>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-700 
             hover:from-blue-600 hover:to-blue-800 
             text-white flex items-center justify-center 
             rounded-xl shadow-lg"
                onClick={toggleModal}
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  className="text-base sm:text-lg"
                />
              </button>

              <button
                onClick={() => setIsUserListCollapsed((prev) => !prev)}
                className="h-10 w-10 bg-gradient-to-br from-teal-500 to-teal-700 
             hover:from-teal-600 hover:to-teal-800 
             text-white flex items-center justify-center 
             rounded-xl shadow-lg"
              >
                {isUserListCollapsed ? <FaAngleUp /> : <FaAngleDown />}
              </button>
            </div>
          </div>
        </div>

        {isOpen && (
          <div className="mb-6 relative z-10">
            <AddSubUser onSuccess={handleSuccess} onCancel={toggleModal} />
          </div>
        )}

        {/* Team Members Grid - Only show when NOT collapsed */}
        {!isUserListCollapsed && (
          <div className="relative z-10">
            {filteredUsers.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 gap-4">
                {filteredUsers.map((user, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      setActiveChat({
                        type: 'subuser',
                        id: user._id,
                        name: user.name,
                      });
                      setIsUserListCollapsed(true);
                      if (isMobileView) setShowChatPanel(true);
                    }}
                    className="flex flex-col items-center cursor-pointer group transition-all duration-300 py-2 rounded-xl hover:bg-white/60 hover:shadow-md"
                  >
                    <div className="relative mb-3">
                      <div
                        className={`w-12 h-12 flex items-center justify-center rounded-full transition-all duration-300 shadow-lg ${
                          activeChat?.id === user._id
                            ? 'bg-blue-600 border-4 border-blue-300'
                            : 'bg-white border-2 border-blue-200 group-hover:border-blue-400 group-hover:shadow-blue-200'
                        }`}
                      >
                        <span
                          className={`font-bold text-lg capitalize ${
                            activeChat?.id === user._id
                              ? 'text-white'
                              : 'text-blue-700 group-hover:text-blue-800'
                          }`}
                        >
                          {user.name.charAt(0) || '?'}
                        </span>
                      </div>

                      {/* Unread Count Badge */}
                      {unreadCounts[user._id] > 0 && (
                        <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[20px] text-center shadow-lg animate-pulse">
                          {unreadCounts[user._id]}
                        </div>
                      )}
                    </div>

                    <div className="text-center">
                      <p
                        className={`font-semibold text-sm capitalize transition-colors ${
                          activeChat?.id === user._id
                            ? 'text-blue-800'
                            : 'text-blue-700 group-hover:text-blue-800'
                        }`}
                      >
                        {user.name || 'Unknown'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-5">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="fa fa-users text-blue-400 text-xl"></i>
                </div>
                <h3 className="text-blue-800 font-semibold text-lg mb-1">
                  No Team Members Found
                </h3>
                <p className="text-blue-600 text-sm mb-4">
                  Add team members to start conversations
                </p>
                <button
                  onClick={toggleModal}
                  className="bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white px-4 py-4 rounded-xl font-semibold transition-all duration-200 hover:shadow-lg"
                >
                  <i className="fa-solid fa-plus mr-2"></i>
                  Add Team Member
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Inbox: Chat */}
      <div
        className={`flex flex-col rounded-xl m-3 mt-0 border shadow-lg overflow-hidden ${
          isMobileView ? (showChatPanel ? 'flex flex-1' : 'hidden') : 'flex-1'
        }`}
      >
        {activeChat ? (
          <>
            {isUserListCollapsed && (
              <div className="flex items-center justify-start rounded-t-xl p-4 bg-white border-b">
                {/* {isMobileView && (
                <button
                  className="mr-2 text-lg px-2 py-1"
                  onClick={() => setShowChatPanel(false)}
                >
                  <i class="fa-solid fa-chevron-left"></i>
                </button>
              )} */}
                {/* Inbox Header */}

                <div className="flex items-center ">
                  <div className="w-10 h-10 bg-[#E9EAEB] border border-[#E9EAEB] flex items-center justify-center rounded-full">
                    <span className="font-semibold text-xl text-[#535862] capitalize">
                      {activeChat.name.charAt(0)}
                    </span>
                  </div>
                  <div className="ml-3">
                    <h6 className="font-semibold text-lg text-black capitalize">
                      {activeChat.name}
                    </h6>
                  </div>
                </div>
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-4 scrollbar-hidden">
              <ul className="space-y-4">
                {messages
                  .slice()
                  .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                  .map((msg, index) => {
                    const msgDate = new Date(
                      msg.timestamp
                    ).toLocaleDateString();
                    const isToday = msgDate === new Date().toLocaleDateString();

                    const showDate =
                      index === 0 ||
                      new Date(
                        messages[index - 1].timestamp
                      ).toLocaleDateString() !== msgDate;
                    return (
                      <li key={index}>
                        {showDate && (
                          <div className="flex items-center my-2">
                            <div className="flex-grow border-t border-gray-300"></div>
                            <span className="mx-2 text-gray-500 ">
                              {isToday ? 'Today' : msgDate}
                            </span>
                            <div className="flex-grow border-t border-gray-300"></div>
                          </div>
                        )}
                        <div
                          className={`flex ${
                            msg.sender === loggedInUserId
                              ? 'justify-end'
                              : 'justify-start'
                          }`}
                        >
                          <div
                            className={`bg-${
                              msg.sender === loggedInUserId
                                ? 'text-end'
                                : ' text-start'
                            } ml-3`}
                          >
                            <div className="flex gap-2">
                              {msg.sender !== loggedInUserId && (
                                <div className="w-10 h-10 bg-[#E9EAEB] mt-1 border border-[#E9EAEB] flex items-center justify-center rounded-full">
                                  <span className="font-semibold text-xl text-[#535862]">
                                    {activeChat.name.charAt(0)}
                                  </span>
                                </div>
                              )}
                              <div>
                                <div className="flex justify-between mb-1">
                                  <div className="text-xs font-semibold text-[#535862]">
                                    {msg.sender === loggedInUserId
                                      ? 'You'
                                      : activeChat.name}
                                  </div>
                                  <div className="flex items-end space-x-2 ml-3">
                                    <span className="text-xs  text-[#535862]">
                                      {new Date(
                                        msg.timestamp
                                      ).toLocaleTimeString()}
                                    </span>
                                  </div>
                                </div>
                                <div
                                  className={`bg-${
                                    msg.sender === loggedInUserId
                                      ? 'themeColor text-white text-justify'
                                      : '[#F5F5F5] text-[#181D27] text-start'
                                  } p-2 rounded-lg max-w-xs break-words`}
                                >
                                  {msg.content}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                <div ref={messagesEndRef}></div>
              </ul>
            </div>
            <div className="flex px-4 gap-2 mb-4 pt-2 bottom-2 w-full">
              <textarea
                value={currentMessage}
                onChange={(e) => {
                  setCurrentMessage(e.target.value);

                  // ✅ Auto-grow like Slack
                  e.target.style.height = 'auto'; // reset first
                  e.target.style.height = `${Math.min(
                    e.target.scrollHeight,
                    400
                  )}px`; // grow until 400px
                }}
                onKeyDown={handleKeyDown}
                className="px-4 py-2 text-lg resize-none w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-themeColor focus:border-themeColor scrollbar-hidden"
                style={{
                  maxHeight: '100px',
                  lineHeight: '1.4',
                  overflowY: 'auto',
                }} // enable scroll if more than 400px
                placeholder="Write a message..."
                rows={1}
              />
              <img
                src="../icons/SendMsg.svg"
                alt="sendIcon"
                onClick={currentMessage.trim() ? handleSendMessage : undefined}
                className={`self-end ${
                  currentMessage.trim()
                    ? 'opacity-100 cursor-pointer'
                    : 'opacity-50 cursor-not-allowed'
                }`}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col h-full rounded-xl bg-gray-100 border relative">
            {userList.length <= 0 && (
              <div className="absolute inset-0 flex justify-center items-center z-50">
                <div className="bg-blue-50 p-6 rounded-xl shadow-lg text-center border border-blue-300 text-blue-700">
                  Add Team Member to start conversation.
                </div>
              </div>
            )}

            {/* <div
              style={{
                background: 'rgba(228, 228, 228, 0.3',
                backdropFilter: 'blur(5px)',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
                borderRadius: '12px',
              }}
            /> */}
            <div
              style={{
                position: 'absolute',
                background: 'rgba(228, 228, 228, 0.3',
                backdropFilter: 'blur(5px)',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <p style={{color: '#6B7280', fontSize: '14px', fontWeight: 500}}>
                Please select a conversation to continue
              </p>
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl bg-white border-b">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-themeColor3 text-gray-800 flex items-center justify-center rounded-full">
                  <span className="font-semibold text-xl">U</span>
                </div>
                <div className="ml-3">
                  <h6 className="font-semibold text-gray-800 text-2xl">User</h6>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <ul className="space-y-4">
                <li className="flex justify-end">
                  <div className="flex items-end space-x-2">
                    <span className="text-xs text-gray-400">
                      10:10 AM, Today
                    </span>
                  </div>
                  <div className="bg-themeColor3 text-gray-800 p-3 rounded-lg max-w-xs ml-3">
                    Hi Aiden, how are you? How is the project coming along?
                  </div>
                </li>
                <li className="flex">
                  <div className="bg-gray-200 text-white p-3 rounded-lg max-w-xs ml-3">
                    Are we meeting today?
                  </div>
                  <div className="flex items-end space-x-2 ml-3">
                    <span className="text-xs text-gray-400">
                      10:12 AM, Today
                    </span>
                  </div>
                </li>
                <li className="flex">
                  <div className="bg-gray-200 text-white p-3 rounded-lg max-w-xs ml-3">
                    Project has been already finished and I have results to show
                    you.
                  </div>
                  <div className="flex items-end space-x-2 ml-3">
                    <span className="text-xs text-gray-400">
                      10:15 AM, Today
                    </span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="p-4 bg-gray-100 rounded-xl">
              <div className="flex items-center">
                <i className="fa-solid fa-plus text-xl mr-3 text-themeColor cursor-pointer"></i>
                <div className="relative w-full">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                    <i className="fa-solid fa-paper-plane"></i>
                  </span>
                  <input
                    type="text"
                    className="pl-10 pr-4 py-2 w-full bg-white border rounded-lg text-sm focus:outline-none focus:ring focus:ring-themeColor3"
                    placeholder="Enter text here..."
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default Chat;
